import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Slider,
  Tooltip,
  Chip,
  Paper,
  LinearProgress,
  Fade,
  Zoom,
  useTheme,
  alpha,
  Breadcrumbs,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  Stack,
  TextField,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  SwapHoriz as SwapHorizIcon,
  ContentCopy as ContentCopyIcon,
  VolumeUp,
  Pause,
  PlayArrow,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Circle,
  AutoFixHigh,
  Translate as TranslateIcon,
  Language as LanguageIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Speed as SpeedIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Psychology,
  ContentCopy,
  Send,
  Edit,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ContentState, Editor, EditorState, RichUtils } from "draft-js";
import {
  checkTranslator,
  checkTranslatorLang,
} from "../../services/translatorServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { addUserLogs } from "../../services/userLogServices";
import { useCheckFeatureAccess } from "../CheckFeature";
import SubscriptionRequired from "../SubscriptionRequired";
import { useCheckSubscriptionPlanAccess } from "../SubscriptionAccess";
import { checkAIRequestLimit } from "../aiRequestLimit";
import { useHistory } from "react-router-dom";
import jwtDecode from "jwt-decode";
import Poetlogopen from "../../static/POET_Logo_Pen.png";
import { withStyles } from "@material-ui/core/styles";
import TranslatorHistory from "./TranslatorHistory";
import { saveTranslationToHistory } from "../../services/translatorHistoryService";
import { aiSmartDesk } from "../../services/aiIntelligenzServices";

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const SelectDropdownMui = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  error,
  ...props
}) => (
  <select
    onChange={onChange}
    value={value}
    style={{
      width: "100%",
      padding: "12px",
      borderRadius: "12px",
      border: error ? "2px solid #f44336" : "2px solid #e3f2fd",
      fontSize: "14px",
      backgroundColor: "white",
      cursor: "pointer",
      transition: "border 0.3s ease",
      boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
    }}
    {...props}
  >
    <option value="">{selectLabel}</option>
    {optionsList.map((item, idx) => (
      <option key={idx} value={item.value}>
        {item.name}
      </option>
    ))}
  </select>
);

const SelectDropdownSearch = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  isDarkMode,
  error,
  inputRef,
  ...props
}) => {
  const borderColor = error ? "#f44336" : "#e3f2fd";
  const backgroundColor = isDarkMode ? "#2A2A2A" : "white";

  return (
    <select
      ref={inputRef}
      onChange={(e) => onChange(e, e.target.value)}
      value={value}
      style={{
        width: "100%",
        padding: "12px",
        borderRadius: "12px",
        border: `2px solid ${borderColor}`,
        fontSize: "14px",
        backgroundColor: backgroundColor,
        color: isDarkMode ? "white" : "black",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
        outline: "none",
      }}
      {...props}
    >
      <option value="">{selectLabel}</option>
      {optionsList.map((lang, idx) => (
        <option key={idx} value={lang}>
          {lang}
        </option>
      ))}
    </select>
  );
};

const Loader = ({ loading }) => (loading ? <LinearProgress /> : null);

const ModernTranslator = () => {
  const theme = useTheme();
  const userDetails = useSelector((user) => user);
  const token = sessionStorage.getItem("token");

  // History state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentMachineId, setCurrentMachineId] = useState(null);

  const getIdentityFromToken = () => {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");

    if (!token) return null;

    try {
      const decoded = jwtDecode(token);

      if (decoded.role === "guest") {
        return { machineId: decoded.machineId };
      } else {
        return { userId: decoded._id };
      }
    } catch (err) {
      console.error("Invalid token");
      return null;
    }
  };

  const identity = getIdentityFromToken();

  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [detectedLanguage, setDetectedLanguage] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [secondaryeditorState, setSecondaryEditorState] = useState(
    EditorState.createEmpty(),
  );
  const [wordsCount, setWordsCount] = useState(0);
  const [opWordsCount, setOpWordsCount] = useState(0);
  const [wordCheck, setWordCheck] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasText, setHasText] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedProfession, setSelectedProfession] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPauseResumeToggle, setShowPauseResumeToggle] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [isSliderMoving, setIsSliderMoving] = useState(false);
  const [speakLanguage, setSpeakLanguage] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [hasTranslatedOnce, setHasTranslatedOnce] = useState(false);
  const [shouldFocusLanguage, setShouldFocusLanguage] = useState(false);

  // State for word limit warning dialog
  const [wordLimitDialogOpen, setWordLimitDialogOpen] = useState(false);
  const [wordLimitExceeded, setWordLimitExceeded] = useState(false);

  const accessToTranslate = useCheckFeatureAccess("translator", "translator");
  const accessToTranslatorScreen = useCheckSubscriptionPlanAccess("Translator");
  const accessToSpeaker = useCheckSubscriptionPlanAccess("speaker");
  const [subReqOpen, setSubReqOpen] = useState(false);
  const [isFeatureInside, setIsFeatureInside] = useState(false);
  const history = useHistory();

  // Refs
  const prevSelectedLanguageRef = useRef("");
  const prevEditorContentRef = useRef("");
  const languageSelectRef = useRef(null);

  // SmartDesk popup state
  const [sdOpen, setSdOpen] = useState(false);
  const [sdConv, setSdConv] = useState([]);
  const [sdMsg, setSdMsg] = useState("");
  const [sdLoading, setSdLoading] = useState(false);
  const [sdEditingId, setSdEditingId] = useState(null);
  const [sdEditContent, setSdEditContent] = useState("");
  const [sdShowSuccess, setSdShowSuccess] = useState(false);
  const [sdSuccessMsg, setSdSuccessMsg] = useState("");
  const sdEndRef = useRef(null);
  const sdGenId = () =>
    Date.now().toString() + Math.random().toString(36).slice(2, 8);

  const languageList = [
    "Arabic", "Assamese", "Bengali", "Cebuano", "Chinese", "Czech", "Danish",
    "Dutch", "English", "Finnish", "French", "German", "Greek", "Gujarati",
    "Hebrew", "Hindi", "Hungarian", "Indonesian", "Italian", "Japanese",
    "Kannada", "Korean", "Malay", "Malayalam", "Marathi", "Nepali", "Norwegian",
    "Odia", "Persian", "Polish", "Portuguese", "Punjabi", "Romanian", "Russian",
    "Sanskrit", "Spanish", "Swedish", "Tagalog", "Tamil", "Telugu", "Thai",
    "Turkish", "Urdu", "Ukrainian", "Vietnamese",
  ];

  const industryList = [
    { value: "IT", name: "IT" },
    { value: "Healthcare", name: "Healthcare" },
    { value: "Finance", name: "Finance" },
    { value: "Agriculture", name: "Agriculture" },
    { value: "Education", name: "Education" },
    { value: "Retail", name: "Retail" },
    { value: "Telecommunication", name: "Telecommunication" },
  ];

  const professionList = [
    { value: "Content Writer", name: "Content Writer" },
    { value: "Business Owner", name: "Business Owner" },
    { value: "HR Professional", name: "HR Professional" },
    { value: "Marketing", name: "Marketing" },
    { value: "Product Manager", name: "Product Manager" },
    { value: "Medical Professional", name: "Medical Professional" },
    { value: "Legal Professional", name: "Legal Professional" },
  ];

  // SmartDesk scroll to bottom
  useEffect(() => {
    sdEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sdConv, sdLoading]);

  // SmartDesk send message
  const sdSend = async (text) => {
    const txt = (text || sdMsg).trim();
    if (!txt || sdLoading) return;
    const userMsg = {
      id: sdGenId(),
      type: "user",
      content: txt,
      ts: new Date(),
    };
    const updated = [...sdConv, userMsg];
    setSdConv(updated);
    setSdMsg("");
    setSdLoading(true);
    try {
      const res = await aiSmartDesk(txt, "");
      const reply =
        res.data?.data?.response ||
        res.data?.response ||
        (typeof res.data === "string" ? res.data : null) ||
        "I couldn't parse that response.";
      setSdConv((prev) => [
        ...prev,
        { id: sdGenId(), type: "ai", content: reply, ts: new Date() },
      ]);
    } catch (e) {
      setSdConv((prev) => [
        ...prev,
        {
          id: sdGenId(),
          type: "ai",
          content: "Error: " + (e.message || "something went wrong"),
          ts: new Date(),
          isError: true,
        },
      ]);
    }
    setSdLoading(false);
  };

  // SmartDesk save edit
  const sdSaveEdit = async () => {
    if (!sdEditContent.trim()) {
      setSdEditingId(null);
      return;
    }
    const idx = sdConv.findIndex((m) => m.id === sdEditingId);
    if (idx === -1) {
      setSdEditingId(null);
      return;
    }
    const updated = [
      ...sdConv.slice(0, idx),
      { ...sdConv[idx], content: sdEditContent.trim(), edited: true },
    ];
    setSdConv(updated);
    setSdEditingId(null);
    setSdEditContent("");
    setSdLoading(true);
    try {
      const res = await aiSmartDesk(sdEditContent.trim(), "");
      const reply =
        res.data?.data?.response ||
        res.data?.response ||
        "Here is my revised response.";
      setSdConv((prev) => [
        ...prev,
        { id: sdGenId(), type: "ai", content: reply, ts: new Date() },
      ]);
    } catch (e) {
      setSdConv((prev) => [
        ...prev,
        {
          id: sdGenId(),
          type: "ai",
          content: "Error: " + e.message,
          ts: new Date(),
        },
      ]);
    }
    setSdLoading(false);
  };

  // Set user identity for history
  useEffect(() => {
    const identity = getIdentityFromToken();
    if (identity) {
      setCurrentUserId(identity.userId);
      setCurrentMachineId(identity.machineId);
    }
  }, []);

  //full screen layout
  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.height = "100vh";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100vh";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.margin = "";
      document.body.style.padding = "";
      document.body.style.height = "";
      document.body.style.overflow = "";
      document.documentElement.style.height = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (accessToTranslatorScreen == false) {
      setSubReqOpen(true);
    }
  }, [accessToTranslatorScreen]);

  useEffect(() => {
    const updateTheme = () => {
      const theme = localStorage.getItem("ThemeSwitch");
      setIsDarkMode(theme === "true");
    };
    updateTheme();
    window.addEventListener("storage", updateTheme);
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "ThemeSwitch") {
        updateTheme();
      }
    };
    return () => {
      window.removeEventListener("storage", updateTheme);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (isPlaying) {
        window.speechSynthesis.cancel();
        setIsPlaying(false);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    console.log("Detector_", inputText, wordCheck);
    if (inputText !== "" && wordCheck !== "" && accessToTranslate) {
      handleDetect();
    }
  }, [wordCheck]);

  // Effect to focus language dropdown when error occurs
  useEffect(() => {
    if (shouldFocusLanguage && languageSelectRef.current) {
      languageSelectRef.current.focus();
      setShouldFocusLanguage(false);

      const languageSection = document.getElementById("translate-to-section");
      if (languageSection) {
        languageSection.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [shouldFocusLanguage]);

  // Effect to auto-translate when language changes after first translation
  useEffect(() => {
    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      selectedLanguage !== prevSelectedLanguageRef.current &&
      inputText &&
      !loading
    ) {
      console.log("Auto-translating due to language change...");
      handleTranslate(false);
    }

    prevSelectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage, inputText]);

  // Effect to auto-translate when source text changes after first translation
  useEffect(() => {
    const currentContent = editorState.getCurrentContent().getPlainText();

    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      currentContent &&
      currentContent !== prevEditorContentRef.current &&
      !loading
    ) {
      console.log("Auto-translating due to source text change...");
      handleTranslate(false);
    }

    prevEditorContentRef.current = currentContent;
  }, [editorState]);

  // Save current translation to history
  const handleSaveToHistory = async () => {
    const translatedContent = secondaryeditorState.getCurrentContent().getPlainText();
    const originalContent = editorState.getCurrentContent().getPlainText();
    
    if (!translatedContent || !originalContent) {
      toast.warn("No translation to save");
      return;
    }
    
    if (!selectedLanguage) {
      toast.warn("Please select a target language");
      return;
    }
    
    const identity = getIdentityFromToken();
    
    try {
      const result = await saveTranslationToHistory({
        userId: identity?.userId || null,
        machineId: identity?.machineId || null,
        originalText: originalContent,
        translatedText: translatedContent,
        sourceLanguage: detectedLanguage || "Auto-detected",
        targetLanguage: selectedLanguage,
        profession: selectedProfession || "",
        industry: selectedIndustry || "",
        title: translatedContent.substring(0, 50),
      });
      
      if (result.success) {
        toast.success("Translation saved to history!");
      } else {
        toast.error("Failed to save translation");
      }
    } catch (error) {
      console.error("Error saving translation:", error);
      toast.error("Failed to save translation");
    }
  };

  // Restore translation from history
  const handleRestoreTranslation = (translatedText) => {
    const contentState = ContentState.createFromText(translatedText);
    const newEditorState = EditorState.createWithContent(contentState);
    setSecondaryEditorState(newEditorState);
    setTranslatedText(translatedText);
    toast.success("Translation restored successfully!");
  };

  const handleListen = () => {
    if (accessToSpeaker == false) {
      setSubReqOpen(true);
      setIsFeatureInside(true);
      return;
    }

    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const textToSpeak = secondaryeditorState
        .getCurrentContent()
        .getPlainText();
      const utterance = new SpeechSynthesisUtterance(textToSpeak);

      const voices = window.speechSynthesis.getVoices();
      console.log("Available Voices:", voices);

      const femaleVoices = voices.filter(
        (voice) =>
          voice.name.includes("Microsoft Zira - English (United States)") &&
          voice.lang.includes(speakLanguage.toLowerCase()),
      );

      console.log("femaleVoices:", femaleVoices);

      if (femaleVoices.length > 0) {
        utterance.voice = femaleVoices[0];
      } else {
        console.warn("Female voice not found");
      }

      utterance.lang = speakLanguage;
      utterance.rate = currentSpeed;

      utterance.onend = () => {
        console.log("Speech synthesis completed");
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      };

      if (!isPlaying) {
        window.speechSynthesis.speak(utterance);
        setIsPlaying(true);
        setShowPauseResumeToggle(true);
      } else {
        window.speechSynthesis.pause();
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      }
    } else {
      console.error("Speech synthesis not supported in this browser.");
    }
  };

  const handleSpeedChange = (_, newValue) => {
    if (!isSliderMoving) {
      setSpeed(newValue);
      setCurrentSpeed(newValue);

      if (isPlaying) {
        const textToSpeak = secondaryeditorState
          .getCurrentContent()
          .getPlainText();
        const utterance = new SpeechSynthesisUtterance(textToSpeak);

        const voices = window.speechSynthesis.getVoices();

        const femaleVoices = voices.filter(
          (voice) =>
            voice.name.includes("Microsoft Zira - English (United States)") &&
            voice.lang.includes(speakLanguage.toLowerCase()),
        );

        if (femaleVoices.length > 0) {
          utterance.voice = femaleVoices[0];
        } else {
          console.warn("Female voice not found. Using default voice.");
        }

        utterance.lang = speakLanguage;
        utterance.rate = newValue;

        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      window.speechSynthesis.pause();
    } else {
      window.speechSynthesis.resume();
    }
    setIsPlaying(!isPlaying);
  };

  const clearListenFunctionality = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setShowPauseResumeToggle(false);
  };

  const handleOuputChange = (newEditorState) => {
    const contentState = newEditorState.getCurrentContent();
    setSecondaryEditorState(newEditorState);

    if (contentState.hasText() === false) {
      clearListenFunctionality();
    }
  };

  const handleDetect = async () => {
    try {
      if (inputText === "") {
        return;
      }
      let text = inputText.split(" ")[0];
      console.log("texttttttttttttt", inputText);
      const response = await checkTranslatorLang(text);
      const detectedLanguage = response.data[0];
      console.log("detectedLanguage:", detectedLanguage);
      setDetectedLanguage(detectedLanguage);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
    }
  };

  // UPDATED handleTranslate - Shows dialog when word count exceeds limit
  const handleTranslate = async (isManual = true) => {
    setLanguageError(false);

    if (!selectedLanguage) {
      if (isManual) {
        setLanguageError(true);
        toast.warn("Please select a language to translate to");
        setShouldFocusLanguage(true);
      }
      setLoading(false);
      return;
    }

    const wordCount = editorState
      .getCurrentContent()
      .getPlainText()
      .trim()
      .split(/\s+/).length;

    if (wordCount > 1500) {
      setWordLimitDialogOpen(true);
      setWordLimitExceeded(true);
      return;
    }

    setLoading(true);
    try {
      let input;
      const selection = editorState.getSelection();
      input = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getAnchorKey())
        .getText()
        .slice(selection.getStartOffset(), selection.getEndOffset());
      if (!input) {
        input = editorState.getCurrentContent().getPlainText();
      }

      if (input === "") {
        if (isManual) {
          toast.warn("Please enter text to translate");
        }
        setLoading(false);
        return;
      }

      console.log(
        "SelectedLang_",
        selectedLanguage,
        selectedIndustry,
        selectedProfession,
      );

      let data;
      if (selectedProfession !== "" && selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          selectedProfession,
          input,
          ...identity,
        };
      } else if (selectedProfession !== "") {
        data = {
          selectedLanguage,
          selectedProfession,
          input,
          ...identity,
        };
      } else if (selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          input,
          ...identity,
        };
      } else {
        data = {
          selectedLanguage,
          input,
          isCoAuthor: false,
          ...identity,
        };
      }

      const response = await checkTranslator(data);
      const translatedText = response?.data?.output;
      console.log("translatedText", translatedText);

      if (response.status == 400) {
        toast.warn(response.message);
        return;
      }

      const data1 = {
        actionType: "translator",
        ...identity,
      };

      const addLogs = await addUserLogs(data1);
      if (addLogs.status !== 200) {
        toast.error(addLogs.message);
      }

      let textWithoutLeadingNewlines = translatedText[0].replace(/^\n+/, "");
      let wordLength = translatedText[0].split(/[ -]+/);

      setOpWordsCount(wordLength.length);
      const contentState = ContentState.createFromText(
        textWithoutLeadingNewlines,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setTranslatedText(translatedText);
      setSecondaryEditorState(newEditorState);

      if (!hasTranslatedOnce) {
        setHasTranslatedOnce(true);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
      toast.warn(error.message);
      setLoading(false);
    }
  };

  const handleWordLimitDialogClose = () => {
    setWordLimitDialogOpen(false);
    setWordLimitExceeded(false);
  };

  const handleBreadcrumbClick = (event, path) => {
    event.preventDefault();
    window.location.href = "/docTags";
  };

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);

    const plainTextContent = newEditorState.getCurrentContent().getPlainText();
    setInputText(plainTextContent);

    if (plainTextContent === "") {
      setDetectedLanguage("");
      setSelectedIndustry("");
      setSelectedProfession("");
      setWordsCount(0);
      setOpWordsCount(0);
      setHasText(false);
    } else {
      setHasText(true);
    }

    if (plainTextContent !== "") {
      const trimmedText = plainTextContent.trim();
      const words = trimmedText
        .split(/\s+/)
        .filter((word) => word !== "").length;
      let splitinput = plainTextContent.split(" ");
      if (splitinput.length > 1) {
        setWordCheck(splitinput[0]);
      }
      console.log("words_", words);
      setWordsCount(words);
    }
  };

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    console.log("editorstate_", newState);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const handlecopy = () => {
    let text = secondaryeditorState.getCurrentContent().getPlainText();
    if (text == "") {
      toast.warn(`You don't have text, to copy`);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        console.log("Async: Copying to clipboard was successful!");
        if (text) toast.info("Your text Copied!");
      },
      function (err) {
        console.error("Async: Could not copy text: ", err);
      },
    );
  };

  const handleDownload = () => {
    if (secondaryeditorState.getCurrentContent().getPlainText() == "") {
      toast.warn(`You don't have text, to download`);
      return;
    }
    const blob = new Blob(
      [secondaryeditorState.getCurrentContent().getPlainText()],
      { type: "text/plain" },
    );

    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "text_content.doc";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleLanguage = (event, value) => {
    console.log("e_", event);
    console.log("V_", value);

    if (value) {
      setLanguageError(false);
    }

    if (value !== selectedLanguage) {
      setSecondaryEditorState(EditorState.createEmpty());
      clearListenFunctionality();
    }
    setSelectedLanguage(value);
  };

  const handleDelete = () => {
    setEditorState(EditorState.createEmpty());
    setSecondaryEditorState(EditorState.createEmpty());
    setInputText("");
    setTranslatedText("");
    setDetectedLanguage("");
    setSelectedIndustry("");
    setSelectedProfession("");
    setSelectedLanguage("");
    setWordsCount(0);
    setOpWordsCount(0);
    setWordCheck("");
    setHasText(false);
    setLanguageError(false);
    setHasTranslatedOnce(false);
    clearListenFunctionality();
  };

  const handleInterchange = () => {
    if (!secondaryeditorState.getCurrentContent().hasText()) {
      return;
    }
    const temp = detectedLanguage;
    console.log("temp_", temp);
    setDetectedLanguage(selectedLanguage);
    setSelectedLanguage(temp);

    const tempText = inputText;
    setInputText(translatedText);
    setTranslatedText(tempText);

    const tempEditorState = editorState;
    console.log("editor_", tempEditorState, secondaryeditorState);
    setEditorState(secondaryeditorState);
    setSecondaryEditorState(tempEditorState);

    const firstEditor = secondaryeditorState.getCurrentContent().getPlainText();
    const secondEditor = tempEditorState.getCurrentContent().getPlainText();
    handleWordsCount(firstEditor, "first");
    handleWordsCount(secondEditor, "second");
  };

  const handleWordsCount = (text, type) => {
    const trimmedText = text.trim();
    const words = trimmedText.split(/\s+/).filter((word) => word !== "").length;
    console.log("words_", words);
    if (type == "first") {
      setWordsCount(words);
    } else {
      setOpWordsCount(words);
    }
  };

  // Check if word limit is exceeded for styling
  const isWordLimitExceeded = wordsCount > 1500;

  return (
    <>
      <SubscriptionRequired
        open={subReqOpen}
        setOpen={setSubReqOpen}
        isFeatureInside={isFeatureInside}
        setIsFeatureInside={setIsFeatureInside}
        path={"translator"}
      />

      {/* Word Limit Warning Dialog */}
      <Dialog
        open={wordLimitDialogOpen}
        onClose={handleWordLimitDialogClose}
        aria-labelledby="word-limit-dialog-title"
        aria-describedby="word-limit-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            padding: 2,
            minWidth: 400,
            background: isDarkMode ? "#1e293b" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
          },
        }}
      >
        <DialogTitle
          id="word-limit-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#ef4444",
            fontWeight: 600,
          }}
        >
          <WarningIcon sx={{ fontSize: 28 }} />
          Word Limit Exceeded
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="word-limit-dialog-description"
            sx={{
              color: isDarkMode ? "#94a3b8" : "#4b5563",
              fontSize: "1rem",
            }}
          >
            You have entered <strong>{wordsCount}</strong> words, which exceeds
            the maximum limit of <strong>1500 words</strong>.
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: isDarkMode ? "#334155" : "#f3f4f6",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? "#e2e8f0" : "#1f2937" }}
            >
              Please reduce your text to 1500 words or less to continue with
              translation.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleWordLimitDialogClose}
            variant="contained"
            sx={{
              bgcolor: "#ef4444",
              "&:hover": {
                bgcolor: "#dc2626",
              },
              borderRadius: 2,
              px: 4,
            }}
          >
            OK, Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          background: isDarkMode
            ? "linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)"
            : "linear-gradient(135deg, #ffffffff 0%, #d3d1d1ff)",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s ease",
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              width: "100%",
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            {/* LEFT SIDE - Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                  },
                }}
              >
                <Breadcrumbs
                  separator={
                    <NavigateNextIcon
                      fontSize="small"
                      sx={{ color: "#667eea" }}
                    />
                  }
                  aria-label="breadcrumb"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        bgcolor: "white",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        mr: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <img
                        alt="logo"
                        src={Poetlogopen}
                        style={{
                          height: "28px",
                          width: "28px",
                          objectFit: "contain",
                          bgcolor: "white",
                          filter:
                            "drop-shadow(0 0 7px rgba(163, 163, 164, 0.65))",
                        }}
                      />
                    </Box>
                    <AutoFixHigh sx={{ fontSize: 20, color: "#667eea" }} />

                    <Link
                      to="/docTags"
                      onClick={(e) => handleBreadcrumbClick(e, "/docTags")}
                      style={{
                        textDecoration: "none",
                        color: "#667eea",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      MultiTool AI
                    </Link>

                    <NavigateNextIcon
                      fontSize="small"
                      sx={{ color: "#667eea" }}
                    />

                    <Typography
                      sx={{
                        color: "#1a1a1a",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(102, 126, 234, 0.08)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      AI Translator
                    </Typography>
                  </Box>
                </Breadcrumbs>
              </Paper>
            </Box>

            {/* CENTER CONTENT */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <TranslateIcon
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "primary.main",
                    mr: 2,
                    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "primary.main",
                    lineHeight: 1.3,
                  }}
                >
                  AI Translator
                </Typography>
              </Box>
            </Box>

            {/* RIGHT SIDE - History Button */}
            <Box sx={{ mr: 14 }}>
              <Tooltip title="View History">
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryOpen(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: "#667eea",
                    color: "#667eea",
                    "&:hover": {
                      borderColor: "#764ba2",
                      backgroundColor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  History
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <Zoom in timeout={1000}>
            <Paper
              elevation={4}
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: "20px",
                background: isDarkMode
                  ? alpha("#2a2a2a", 0.9)
                  : alpha("#ffffff", 0.95),
                backdropFilter: "blur(15px)",
                flexShrink: 0,
              }}
            >
              <Grid2 container spacing={3} alignItems="center">
                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <LanguageIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Detected Language
                      </Typography>
                    </Box>
                    <Chip
                      icon={<LanguageIcon sx={{ fontSize: 16 }} />}
                      label={detectedLanguage || "Auto-detect"}
                      variant={detectedLanguage ? "filled" : "outlined"}
                      color={detectedLanguage ? "primary" : "default"}
                      sx={{
                        minWidth: "150px",
                        fontWeight: 600,
                        height: "40px",
                        fontSize: "0.9rem",
                        borderRadius: "10px",
                        backgroundColor: detectedLanguage
                          ? "primary.main"
                          : isDarkMode
                            ? "black.800"
                            : "black.100",
                        color: detectedLanguage ? "white" : "text.secondary",
                      }}
                    />
                  </Box>
                </Grid2>

                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <WorkIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Profession
                      </Typography>
                    </Box>
                    <SelectDropdownMui
                      optionsList={professionList}
                      selectLabel="Select..."
                      onChange={(e) => setSelectedProfession(e.target.value)}
                      value={selectedProfession}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "160px",
                          "& .MuiSelect-select": {
                            color: selectedProfession
                              ? "text.primary"
                              : "text.disabled",
                            fontWeight: selectedProfession ? 500 : 400,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "text.disabled",
                        },
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <BusinessIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Industry
                      </Typography>
                    </Box>
                    <SelectDropdownMui
                      optionsList={industryList}
                      selectLabel="Select..."
                      onChange={(e) => setSelectedIndustry(e.target.value)}
                      value={selectedIndustry}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "160px",
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                            paddingRight: "32px !important",
                            color: selectedIndustry
                              ? "text.primary"
                              : "text.disabled",
                            fontWeight: selectedIndustry ? 500 : 400,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "text.disabled",
                        },
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={2}>
                  <Box id="translate-to-section">
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <TranslateIcon
                        sx={{
                          fontSize: 18,
                          color: languageError ? "#f44336" : "text.secondary",
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: languageError ? "#f44336" : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        Translate To
                        <span
                          style={{
                            color: "#f44336",
                            marginLeft: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          *
                        </span>
                      </Typography>
                    </Box>
                    <SelectDropdownSearch
                      optionsList={languageList}
                      selectLabel="Select..."
                      onChange={handleLanguage}
                      value={selectedLanguage}
                      isDarkMode={isDarkMode}
                      error={languageError}
                      inputRef={languageSelectRef}
                    />
                    {languageError && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f44336",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        Please select a language to translate to
                      </Typography>
                    )}
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={1.5}>
                  <Box
                    sx={{ textAlign: "center", mt: { xs: 2, sm: 3 }, mb: 0.8 }}
                  >
                    <LightTooltip title="Clear All Content">
                      <Button
                        onClick={handleDelete}
                        variant="outlined"
                        color="error"
                        startIcon={<ClearIcon />}
                        disabled={!hasText}
                        sx={{
                          mt: 0.4,
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "120px",
                          textTransform: "none",
                          fontWeight: 600,
                          borderWidth: "2px",
                          "&:hover": {
                            borderWidth: "2px",
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.04,
                            ),
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 8px rgba(244, 67, 54, 0.2)",
                          },
                          transition: "all 0.2s ease",
                          "&:disabled": {
                            borderColor: "black.300",
                            color: "black.500",
                          },
                        }}
                      >
                        Clear All
                      </Button>
                    </LightTooltip>
                  </Box>
                </Grid2>
              </Grid2>
            </Paper>
          </Zoom>

          <Loader loading={loading} />

          {/* Main content area - takes remaining height */}
          <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
            <Grid2 container spacing={3} sx={{ height: "100%" }}>
              <Grid2 xs={12} md={6} sx={{ height: "100%" }}>
                <Fade in timeout={1400}>
                  <Card
                    elevation={8}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      overflow: "hidden",
                      background: isDarkMode
                        ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                        : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          background:
                            "linear-gradient(90deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))",
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Circle sx={{ fontSize: 12, mr: 1 }} />
                              Source Text
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <LightTooltip
                              title="Swap Languages"
                              placement="top"
                            >
                              <IconButton
                                onClick={handleInterchange}
                                disabled={
                                  !secondaryeditorState
                                    .getCurrentContent()
                                    .hasText()
                                }
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  color: "white",
                                  width: 38,
                                  height: 37,
                                  borderRadius: "20px",
                                  boxShadow:
                                    "0 4px 12px rgba(102, 126, 234, 0.3)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                                    transform: "translateY(-2px)",
                                    boxShadow:
                                      "0 6px 16px rgba(102, 126, 234, 0.4)",
                                  },
                                  "&:active": {
                                    transform: "translateY(0)",
                                  },
                                  "&:disabled": {
                                    background: "black.300",
                                    color: "black.500",
                                    boxShadow: "none",
                                    transform: "none",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <SwapHorizIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </LightTooltip>
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          p: 2,
                          "& .DraftEditor-root": {
                            height: "100%",
                            overflow: "auto",
                            fontSize: "16px",
                            lineHeight: 1.6,
                            color: isDarkMode ? "white" : "text.primary",
                          },
                          "& .public-DraftEditor-content": {
                            height: "100%",
                          },
                          "& .public-DraftEditorPlaceholder-root": {
                            color: "text.secondary",
                            fontSize: "16px",
                          },
                        }}
                      >
                        <Editor
                          editorState={editorState}
                          onChange={handleEditorChange}
                          placeholder="Enter your text to translate..."
                        />
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          background: alpha(
                            theme.palette.background.paper,
                            0.5,
                          ),
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Chip
                          icon={<Circle sx={{ fontSize: "12px !important" }} />}
                          label={`${wordsCount}/1500 Words`}
                          variant="outlined"
                          size="small"
                          sx={{
                            bgcolor: isWordLimitExceeded ? "#ef4444" : "inherit",
                            color: isWordLimitExceeded ? "white" : "inherit",
                            "& .MuiChip-label": {
                              color: isWordLimitExceeded ? "white" : "inherit",
                            },
                          }}
                        />

                        {/* UPDATED Translate Button - Now clickable with red color when limit exceeded */}
                        <Button
  variant="contained"
  onClick={() => handleTranslate(true)}
  disabled={!hasText || !accessToTranslate || loading}
  startIcon={<TranslateIcon />}
  sx={{
    borderRadius: "12px",
    px: 3,
    py: 1,
    background: isWordLimitExceeded
      ? "#ef4444"
      : "linear-gradient(45deg, #75dab1ff, #38885faf)",
    "&:hover": {
      background: isWordLimitExceeded
        ? "#dc2626"
        : "linear-gradient(45deg, #5a6fd8, #6a4190)",
      transform: "translateY(-2px)",
      boxShadow: isWordLimitExceeded
        ? "0 8px 25px rgba(239,68,68,0.4)"
        : "0 8px 25px rgba(102,126,234,0.4)",
    },
    "&:disabled": {
      background: "black.300",
    },
    transition: "all 0.2s ease",
  }}
>
  {loading ? "Translating..." : "Translate"}
</Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid2>
              <Grid2 xs={12} md={6} sx={{ height: "100%" }}>
                <Fade in timeout={1400}>
                  <Card
                    elevation={8}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      overflow: "hidden",
                      background: isDarkMode
                        ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                        : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                      backdropFilter: "blur(10px)",
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          background:
                            "linear-gradient(90deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))",
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "secondary.main",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Circle sx={{ fontSize: 12, mr: 1 }} />
                          Translation
                          {selectedLanguage && (
                            <Chip
                              label={selectedLanguage}
                              size="small"
                              sx={{ ml: 2, fontWeight: 600 }}
                            />
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          p: 2,
                          "& .DraftEditor-root": {
                            height: "100%",
                            overflow: "auto",
                            fontSize: "16px",
                            lineHeight: 1.6,
                            color: isDarkMode ? "white" : "text.primary",
                          },
                          "& .public-DraftEditor-content": {
                            height: "100%",
                          },
                        }}
                      >
                        <Editor
                          editorState={secondaryeditorState}
                          onChange={handleOuputChange}
                          readOnly={true}
                          placeholder="Translation will appear here..."
                        />
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          background: alpha(
                            theme.palette.background.paper,
                            0.5,
                          ),
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Chip
                          icon={<Circle sx={{ fontSize: "12px !important" }} />}
                          label={`${opWordsCount}/1500 Words`}
                          variant="outlined"
                          size="small"
                          color="secondary"
                        />

                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <LightTooltip title="Save to History">
                            <IconButton
                              onClick={handleSaveToHistory}
                              disabled={!opWordsCount}
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    "#f59e0b",
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                          </LightTooltip>

                          <LightTooltip title="Download">
                            <IconButton
                              onClick={handleDownload}
                              disabled={!opWordsCount}
                              sx={{
                                color: "success.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.success.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </LightTooltip>

                          <LightTooltip title="Copy">
                            <IconButton
                              onClick={handlecopy}
                              disabled={!opWordsCount}
                              sx={{
                                color: "info.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </LightTooltip>

                          <LightTooltip title="Listen">
                            <IconButton
                              onClick={handleListen}
                              disabled={!opWordsCount}
                              sx={{
                                color: "warning.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.warning.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <VolumeUp />
                            </IconButton>
                          </LightTooltip>

                          {showPauseResumeToggle && (
                            <>
                              <LightTooltip
                                title={isPlaying ? "Pause" : "Resume"}
                              >
                                <IconButton
                                  onClick={handleTogglePlay}
                                  sx={{ color: "text.secondary" }}
                                >
                                  {isPlaying ? <Pause /> : <PlayArrow />}
                                </IconButton>
                              </LightTooltip>

                              <Box sx={{ width: 100, ml: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <SpeedIcon
                                    sx={{
                                      fontSize: 16,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Slider
                                    value={speed}
                                    min={0.25}
                                    max={2}
                                    step={0.25}
                                    onChange={handleSpeedChange}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${value}x`}
                                    size="small"
                                    sx={{
                                      "& .MuiSlider-thumb": {
                                        width: 16,
                                        height: 16,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid2>
            </Grid2>
          </Box>
        </Container>

        {/* History Drawer */}
        <TranslatorHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onRestoreTranslation={handleRestoreTranslation}
          userId={currentUserId}
          machineId={currentMachineId}
        />

        {/* ── Wave FAB for AI SmartDesk ── */}
        <Box
          sx={{
            position: "fixed",
            top: 30,
            right: 28,
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: 60,
                height: 60,
                borderRadius: "50%",
                border: "2.5px solid rgba(102,126,234,0.5)",
                animation: "sdWave 2s ease-out infinite",
                animationDelay: `${i * 0.6}s`,
                "@keyframes sdWave": {
                  "0%": { transform: "scale(1)", opacity: 0.8 },
                  "100%": { transform: "scale(2.3)", opacity: 0 },
                },
              }}
            />
          ))}
          <LightTooltip title="AI SmartDesk" placement="left">
            <Box
              onClick={() => setSdOpen(true)}
              sx={{
                position: "relative",
                zIndex: 2,
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                animation: "sdPulse 3s ease-in-out infinite",
                "@keyframes sdPulse": {
                  "0%,100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.07)" },
                },
                "&:hover": {
                  transform: "scale(1.12) !important",
                  boxShadow: "0 8px 24px rgba(102,126,234,0.55)",
                },
                transition: "box-shadow .2s",
                boxShadow: "0 4px 16px rgba(102,126,234,0.45)",
              }}
            >
              <Psychology sx={{ color: "white", fontSize: 28 }} />
            </Box>
          </LightTooltip>
        </Box>

        {/* ── AI SmartDesk Center Dialog ── */}
        <Dialog
          open={sdOpen}
          onClose={() => {
            setSdOpen(false);
            setSdEditingId(null);
          }}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: 480,
              height: 600,
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: isDarkMode ? "#1e293b" : "#fff",
              boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: "linear-gradient(135deg,#667eea,#764ba2)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Avatar
              sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 38, height: 38 }}
            >
              <Psychology sx={{ fontSize: 22, color: "white" }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  fontSize: "0.9rem",
                }}
              >
                AI SmartDesk
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "#4ade80",
                    display: "inline-block",
                  }}
                />
                Online · Ready to assist
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setSdOpen(false);
                setSdEditingId(null);
              }}
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                width: 30,
                height: 30,
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              bgcolor: isDarkMode ? "#0f172a" : "#f8fafc",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(102,126,234,0.25)",
                borderRadius: 4,
              },
            }}
          >
            {sdConv.length === 0 && !sdLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  textAlign: "center",
                  py: 3,
                  minHeight: 320,
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 3,
                    bgcolor: "rgba(102,126,234,0.09)",
                    border: "1px solid rgba(102,126,234,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Psychology sx={{ fontSize: 28, color: "#667eea" }} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? "white" : "#0f172a",
                  }}
                >
                  Hello! I'm your AI assistant
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    maxWidth: 210,
                    lineHeight: 1.6,
                  }}
                >
                  Ask me anything about translation or language
                </Typography>
              </Box>
            ) : (
              sdConv.map((msg) => {
                const isUser = msg.type === "user";
                const isEditing = sdEditingId === msg.id;
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent: isUser ? "flex-end" : "flex-start",
                      gap: 1,
                      alignItems: "flex-end",
                    }}
                  >
                    {!isUser && (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "#764ba2",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                        }}
                      >
                        AI
                      </Avatar>
                    )}
                    <Box sx={{ maxWidth: "72%" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: isUser
                            ? "14px 4px 14px 14px"
                            : "4px 14px 14px 14px",
                          background: isUser
                            ? "linear-gradient(135deg,#667eea,#764ba2)"
                            : isDarkMode
                              ? "#1e293b"
                              : "#fff",
                          border: !isUser
                            ? `0.5px solid ${isDarkMode ? "#334155" : "rgba(0,0,0,0.09)"}`
                            : "none",
                          boxShadow: isUser
                            ? "0 2px 8px rgba(102,126,234,0.25)"
                            : "0 1px 4px rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8rem",
                            lineHeight: 1.65,
                            whiteSpace: "pre-wrap",
                            color: isUser
                              ? "white"
                              : isDarkMode
                                ? "#e2e8f0"
                                : "#0f172a",
                          }}
                        >
                          {msg.content}
                          {msg.edited && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.65rem",
                                color: isUser
                                  ? "rgba(255,255,255,0.7)"
                                  : "#f59e0b",
                                ml: 0.5,
                              }}
                            >
                              (edited)
                            </Box>
                          )}
                        </Typography>
                      </Paper>

                      {/* Edit box for user messages */}
                      {isEditing && (
                        <Box
                          sx={{
                            mt: 0.75,
                            p: 1.25,
                            bgcolor: isDarkMode ? "#1e3a5f" : "#fffbf0",
                            border: `1px solid ${isDarkMode ? "#2563eb" : "#fcd34d"}`,
                            borderRadius: 2,
                          }}
                        >
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={sdEditContent}
                            onChange={(e) => setSdEditContent(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sdSaveEdit();
                              }
                              if (e.key === "Escape") {
                                setSdEditingId(null);
                                setSdEditContent("");
                              }
                            }}
                            autoFocus
                            sx={{
                              mb: 1,
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.78rem",
                                borderRadius: 2,
                                bgcolor: isDarkMode ? "#0f172a" : "#fff",
                                color: isDarkMode ? "#e2e8f0" : "#0f172a",
                              },
                            }}
                          />
                          <Stack
                            direction="row"
                            spacing={0.75}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="small"
                              onClick={() => {
                                setSdEditingId(null);
                                setSdEditContent("");
                              }}
                              variant="outlined"
                              sx={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                                borderColor: "rgba(0,0,0,0.15)",
                                borderRadius: 1.5,
                                px: 1.5,
                                py: 0.4,
                                textTransform: "none",
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              onClick={sdSaveEdit}
                              variant="contained"
                              sx={{
                                fontSize: "0.72rem",
                                background:
                                  "linear-gradient(135deg,#667eea,#764ba2)",
                                borderRadius: 1.5,
                                px: 1.5,
                                py: 0.4,
                                textTransform: "none",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg,#5a6fd6,#6a4190)",
                                },
                              }}
                            >
                              Save & resend
                            </Button>
                          </Stack>
                        </Box>
                      )}

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#94a3b8",
                          display: "block",
                          mt: 0.3,
                          textAlign: isUser ? "right" : "left",
                        }}
                      >
                        {msg.ts.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>

                      {/* Action icons */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mt: 0.4,
                          justifyContent: isUser ? "flex-end" : "flex-start",
                          opacity: 0,
                          transition: "opacity .2s",
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        {isUser && (
                          <LightTooltip title="Edit message">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSdEditingId(msg.id);
                                setSdEditContent(msg.content);
                              }}
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                bgcolor: "rgba(102,126,234,0.1)",
                                "&:hover": { bgcolor: "rgba(102,126,234,0.22)" },
                              }}
                            >
                              <Edit sx={{ fontSize: 13, color: "#667eea" }} />
                            </IconButton>
                          </LightTooltip>
                        )}
                        <LightTooltip
                          title={isUser ? "Copy message" : "Copy response"}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              navigator.clipboard.writeText(msg.content);
                              const btn = e.currentTarget;
                              btn.style.background = "rgba(16,185,129,0.25)";
                              setTimeout(
                                () => (btn.style.background = "rgba(16,185,129,0.1)"),
                                1800
                              );
                              setSdSuccessMsg(
                                isUser ? "Message copied!" : "Response copied!"
                              );
                              setSdShowSuccess(true);
                            }}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: "rgba(16,185,129,0.1)",
                              "&:hover": { bgcolor: "rgba(16,185,129,0.22)" },
                            }}
                          >
                            <ContentCopyIcon
                              sx={{ fontSize: 13, color: "#10b981" }}
                            />
                          </IconButton>
                        </LightTooltip>
                      </Box>
                    </Box>
                    {isUser && (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "#667eea",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                        }}
                      >
                        U
                      </Avatar>
                    )}
                  </Box>
                );
              })
            )}

            {/* Typing indicator */}
            {sdLoading && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "#764ba2",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                  }}
                >
                  AI
                </Avatar>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: "4px 14px 14px 14px",
                    bgcolor: isDarkMode ? "#1e293b" : "#fff",
                    border: `0.5px solid ${isDarkMode ? "#334155" : "rgba(0,0,0,0.09)"}`,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.6, alignItems: "center" }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#a855f7",
                          animation: "sdBounce 1.2s infinite",
                          animationDelay: `${i * 0.18}s`,
                          "@keyframes sdBounce": {
                            "0%,60%,100%": { transform: "translateY(0)" },
                            "30%": { transform: "translateY(-5px)" },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}
            <div ref={sdEndRef} />
          </Box>

          {/* Word count */}
          {sdMsg && (
            <Typography
              variant="caption"
              sx={{
                px: 2,
                pb: 0.5,
                textAlign: "right",
                display: "block",
                color:
                  sdMsg.trim().split(/\s+/).length > 1500 ? "#ef4444" : "#94a3b8",
                bgcolor: isDarkMode ? "#1e293b" : "#fff",
                fontSize: "0.7rem",
              }}
            >
              {sdMsg.trim().split(/\s+/).length} / 1500 words
            </Typography>
          )}

          {/* Input area */}
          <Box
            sx={{
              p: 1.25,
              borderTop: `0.5px solid ${isDarkMode ? "#334155" : "rgba(0,0,0,0.08)"}`,
              display: "flex",
              gap: 1,
              alignItems: "flex-end",
              bgcolor: isDarkMode ? "#1e293b" : "#fff",
              flexShrink: 0,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder="Type your message… (Enter to send)"
              value={sdMsg}
              onChange={(e) => setSdMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sdSend();
                }
              }}
              disabled={sdLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontSize: "0.8rem",
                  bgcolor: isDarkMode ? "#0f172a" : "#f8fafc",
                  color: isDarkMode ? "#e2e8f0" : "#0f172a",
                  "& fieldset": {
                    borderColor: isDarkMode ? "#334155" : "rgba(0,0,0,0.15)",
                  },
                  "&:hover fieldset": { borderColor: "#667eea" },
                  "&.Mui-focused fieldset": { borderColor: "#667eea" },
                },
              }}
            />
            <IconButton
              onClick={() => sdSend()}
              disabled={!sdMsg.trim() || sdLoading}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                flexShrink: 0,
                mb: "2px",
                background:
                  sdMsg.trim() && !sdLoading
                    ? "linear-gradient(135deg,#667eea,#764ba2)"
                    : isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)",
                color:
                  sdMsg.trim() && !sdLoading
                    ? "white"
                    : isDarkMode
                      ? "#475569"
                      : "#94a3b8",
                "&:hover:not(:disabled)": {
                  transform: "scale(1.08)",
                  boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                },
                "&:disabled": {
                  background: isDarkMode
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.08)",
                },
                transition: "all .2s",
              }}
            >
              <Send sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Dialog>

        {/* Success snackbar for copy actions */}
        <Snackbar
          open={sdShowSuccess}
          autoHideDuration={2500}
          onClose={() => setSdShowSuccess(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            sx={{
              borderRadius: 2,
              bgcolor: isDarkMode ? "#065f46" : undefined,
              color: isDarkMode ? "white" : undefined,
              fontWeight: 500,
            }}
          >
            {sdSuccessMsg}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ModernTranslator;